import React, { Component } from 'react';
import { Container, Row, Col, Breadcrumb, Badge } from 'react-bootstrap';
import SessionListView from './sessions/index';
import SessionView from './session/index';
import ExecutionView from './execution/index';
import Navigation from 'context/nav';
import Preferences, { withPreferences } from 'context/preferences';
import Alias from 'components/controls/alias';

export class DebuggerShell extends Component {
  /**
   * The following routes are supported here:
   *
   * - /:orgId/:blobId/debugger
   *   Entry point, renders the list of sessions
   *
   * - /:orgId/:blobId/debugger/:sessionId
   *   Renders session details, list of executions
   *
   * - /:orgId/:blobId/debugger/:sessionId/:executionId
   *   Execution tracking
   */

  constructor(props) {
    super(props);
    // The breadcrumb labels a session by name when one is available. This shell has no session of
    // its own — it renders from the route param — and a breadcrumb is not worth a network
    // round-trip, so it takes the record SessionView has already fetched and falls back to the id.
    //
    // sessionId/executionId are retained here, not re-derived from match.params on every render:
    // match.params is shared with the other two tabs (`:section/:sectionId/:subSectionId`), and this
    // shell stays mounted (just hidden) while another tab is active. Deriving on every render was the
    // bug — it forced both to null the moment the URL left "debugger", tearing down SessionView/
    // ExecutionView underneath the hidden pane. See componentDidUpdate for how they are kept in sync
    // while the Debugger *is* active.
    const section = props.match.params.section;
    this.state = {
      session: null,
      sessionId: ( "debugger" === section ) ? props.match.params.sectionId : null,
      executionId: ( "debugger" === section ) ? props.match.params.subSectionId : null,
    };
  }

  // Keeps sessionId/executionId in sync with the route while the Debugger is the active tab. While
  // another tab is active this is a no-op: match.params still changes underneath (it is shared
  // across tabs), but it belongs to that other tab and must not be adopted, so the retained values
  // are left exactly as they were.
  //
  // A bare `/debugger` (no sectionId) is ambiguous on its own — it is BOTH what the Debugger tab's
  // own link points at (routeForDescriptor, Blob/index.js) and what "clear the session" looks like —
  // and the two must behave oppositely. `prevProps` is what tells them apart:
  //   - RE-ENTERING from elsewhere (prevProps' section was not "debugger"): the shell was mounted
  //     the whole time and never lost its retained id, so the bare path is just the tab link
  //     catching up with where the user actually is, not a request to leave the session. Restore the
  //     retained path with history.replace — the same correction Playground's syncDefinitionPath
  //     makes on its own visibility edge (playground/index.js:404-412, :500-502) — and a tick later
  //     the route agrees with state again.
  //   - Already on the Debugger, and the route becomes bare while staying there: the user clicked
  //     the already-active tab or the Sessions breadcrumb, and means it. Clear.
  componentDidUpdate(prevProps) {
    const section = this.props.match.params.section;
    if ( "debugger" !== section ) {
      return;
    }
    const sessionId = this.props.match.params.sectionId;
    const executionId = this.props.match.params.subSectionId;

    const reentering = ( "debugger" !== prevProps.match.params.section );
    if ( reentering && !sessionId && this.state.sessionId ) {
      const orgId = this.props.match.params.orgId;
      const blobId = this.props.match.params.blobId;
      this.props.history.replace(
        Navigation.workflowPath(orgId, blobId, "debugger", this.state.sessionId, this.state.executionId));
      return;
    }

    if ( sessionId !== this.state.sessionId || executionId !== this.state.executionId ) {
      this.setState({ sessionId: sessionId, executionId: executionId });
    }
  }

  onSessionLoaded = (session) => {
    this.setState({ session: session });
  }

  onClick(event, path) {
    event.preventDefault();
    this.props.history.push(path);
  }

  render() {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    const sessionId = this.state.sessionId;
    const executionId = this.state.executionId;

    // Only trust a lifted record that belongs to the session currently routed to — otherwise
    // navigating from one session to another would briefly label the new route with the old name.
    const session = ( this.state.session && this.state.session.id === sessionId ) ? this.state.session : null;

    const canEditRevision = this.props.app.api.canUpdateRevision(this.props.blob, this.props.revision);
    const timezone = this.props.preferences["app.timezone"];

    const rootLink = ( sessionId ? true : false );
    const sessionLink = ( executionId ? true : false );
    const executionLink = false;

    return (
      <>
        <Container className="blob-header-row content-row">
          <Row>
            <Col>
              <Breadcrumb listProps={{ className: "shell-breadcrumb" }}>
                <Breadcrumb.Item
                  onClick={(event) => {
                    this.onClick(event, Navigation.workflowPath(orgId, blobId, "debugger"))
                  }}
                  active={!rootLink}>
                  Sessions
                </Breadcrumb.Item>
                {sessionId &&
                  <>
                    <Breadcrumb.Item
                      onClick={(event) => {
                        this.onClick(event, Navigation.workflowPath(orgId, blobId, "debugger", sessionId))
                      }}
                      active={!sessionLink}>
                      Session&nbsp;&nbsp;
                      {( session && session.alias )
                        ? <Alias value={session.alias} />
                        : <Badge className="title-tag-badge">{sessionId}</Badge>}
                    </Breadcrumb.Item>

                    {executionId &&
                      <Breadcrumb.Item
                        onClick={(event) => {
                          this.onClick(event, Navigation.workflowPath(orgId, blobId, "debugger", sessionId, executionId))
                        }}
                        active={!executionLink}>
                        Execution&nbsp;&nbsp;
                        <Badge className="title-tag-badge">{executionId}</Badge>
                      </Breadcrumb.Item>
                    }
                  </>
                }
              </Breadcrumb>
            </Col>
          </Row>
        </Container>

        {!sessionId &&
          <SessionListView
            app={this.props.app}
            blob={this.props.blob}
            revision={this.props.revision}
            canEditRevision={canEditRevision}
            match={this.props.match}
            history={this.props.history}
            location={this.props.location}
            timezone={timezone}
          />
        }
        {/* Keyed by id: retention keeps the view for the same id, but a different one — deep-linked from
            another tab while this pane was hidden — must remount, as both views load only on mount. */}
        {( sessionId && !executionId ) &&
          <SessionView
            key={sessionId}
            app={this.props.app}
            blob={this.props.blob}
            revision={this.props.revision}
            canEditRevision={canEditRevision}
            match={this.props.match}
            history={this.props.history}
            location={this.props.location}
            timezone={timezone}
            sessionId={sessionId}
            onSessionLoaded={this.onSessionLoaded}
            shown={this.props.shown}
          />
        }
        {( sessionId && executionId ) &&
          <ExecutionView
            key={executionId}
            app={this.props.app}
            blob={this.props.blob}
            revision={this.props.revision}
            canEditRevision={canEditRevision}
            match={this.props.match}
            history={this.props.history}
            location={this.props.location}
            timezone={timezone}
            executionId={executionId}
            shown={this.props.shown}
          />
        }
      </>
    );
  }
}

export default withPreferences(DebuggerShell, ["app.timezone"],
  props => ({ orgId: props.org.id, blobId: props.blob.id }));
